/**
 * Phase 6c PR 1 — `<TodayHero />` — replaces the Greeting + HeroToday split.
 *
 * One unified hero card on every `/v2/today` state. Eyebrow + serif h1 +
 * optional meta rows + status footer (phase pill + meta text + plan link).
 *
 * Variants (discriminated union):
 *   - `default`  — happy path / no race / no plan / recovery. Eyebrow shows
 *                  date (+ optional greeting + athlete name); body shows
 *                  meta rows; footer shows phase pill + status text + plan
 *                  link.
 *   - `firstRun` — pre-onboarding. Eyebrow says WELCOME; body has a sub
 *                  paragraph + a CTA row instead of the meta + footer.
 *
 * Responsive sizing: a single component, NOT a tier prop. Type ramps via
 * @media on `--bp-comfortable / --bp-default / --bp-wide / --bp-ultra`
 * so a single mounted instance crosses tiers without re-mounting.
 *
 * Sourced from
 * `.claude/references/phase-6c-dashboard-design/mockups/styles.css §"Hero card"`
 * and
 * `.claude/references/phase-6c-dashboard-design/direction.md §3.1`.
 *
 * Notes for callers:
 * - Greeting + HeroToday + HeroTodayDesktop are NOT removed by this PR —
 *   PR 4 (page assembly) is what swaps `routes/today.tsx` to `<TodayHero />`
 *   and deletes the old hero components.
 * - The `date` is rendered with `Intl.DateTimeFormat` for the user's
 *   locale; format is "FRIDAY · MAY 8" by design.
 * - `planLink.href` is consumed verbatim — usually `/v2/plan` or
 *   `/v2/plan/week/:n`.
 */

import { createContext, useContext, type ReactNode } from 'react';
import type { HeroActualSubline } from '../../lib/actualForPlanDay';
import { Icon, type IconName } from '../icons/registry';
import { useIconSystem } from '../../hooks/useIconSystem';
import { contextCoord } from '../../lib/contextCoords';
// REL-8 — the Apple Weather mark, shared with the run summary and chat.
import { AppleWeatherMark } from '../attribution/AppleWeatherMark';
import styles from './TodayHero.module.css';

export type SeasonPhase = 'base' | 'build' | 'competition' | 'taper' | 'recovery';

export type HeroMetaItem = {
  /** Optional uppercase label, e.g. "Pace". When omitted, `v` reads as a plain meta value. */
  k?: string;
  /** Required value, e.g. "Conversational" or "57° overcast · SE 6 mph". */
  v: string;
  /**
   * CD visual polish §1 — an optional decorative glyph that rides INSIDE the
   * value, before the text (the Weather row's condition mark). Decorative by
   * construction: `aria-hidden`, no label, `--ink-500` so the value text keeps
   * the `--ink-900` weight. Omitted → the value renders exactly as before,
   * which is also what an unknown condition word must produce. There is no
   * placeholder glyph.
   */
  glyph?: IconName;
};

export type HeroCTA = {
  label: string;
  href?: string;
  onClick?: () => void;
  variant?: 'primary' | 'secondary';
};

export type TodayHeroDefaultProps = {
  variant: 'default';
  /** "FRIDAY · MAY 8" — the date shown in the eyebrow. */
  date: Date;
  /** Optional name appended to the eyebrow as "GOOD AFTERNOON, RAMIRO". */
  athleteName?: string;
  /** Optional streak chip ("12-day streak"). 0 → not shown. */
  streakDays?: number;
  /** h1 title, e.g. "Easy run, 4 miles — conversational, just the legs." */
  title: string;
  /** Optional meta rows (Pace / Gear / Weather / Route). */
  meta?: HeroMetaItem[];
  /**
   * REL-8 — the meta row's Weather value came from Apple WeatherKit, so the
   * Apple Weather mark is required beside it.
   *
   * ⛔ NOT derivable from `meta` alone. A Weather row can also hold the
   * `?user=ramiro` demo fixture, which no WeatherKit call produced; the caller
   * is the only layer that knows which it handed over. Absent → no mark, which
   * is the correct default for every surface that is not showing real Apple
   * data.
   */
  weatherAttribution?: boolean;
  /** Optional phase pill in the status footer. */
  phase?: SeasonPhase;
  /** Optional week-of-total for the status footer ("Week 5 of 7"). */
  weekOfTotal?: [number, number];
  /** Optional days-to-next-race countdown ("4 days to State Meet"). */
  daysToNextRace?: number;
  /** Optional race name to pair with `daysToNextRace`. */
  nextRaceName?: string;
  /** Optional weekly mileage chip ("27 mi this week"). */
  weekMileage?: number;
  /** Optional "Open week plan ›" link in the footer. */
  planLink?: { label: string; href: string };
  /** State D — show "Plan not yet drafted" instead of week + race counts. */
  planNotDrafted?: boolean;
  /**
   * SP-8 + B-4/B-5 (today_density_v1) — suppress the status footer entirely
   * (phase pill + week/countdown/mileage text + plan link) while KEEPING the
   * ambient mode accent (the `data-mode` left rule + eyebrow dot). In the
   * density layout every footer fact moves to the Plan ledger / race card
   * (decision 1), but the hero still reads its mode hue from `phase`. Default
   * undefined = footer renders exactly as before (byte-identical) — only the
   * flag-ON density path passes this.
   */
  suppressFooter?: boolean;
  /**
   * B-19 (b19_actuals_v1 + today_density_v1 + actual_distance_capture_v1) —
   * the Today-hero COMPLETED-STATE sub-line (frame §1-§8). A quiet factual line
   * ("You ran 5.0 mi · planned 4") that mounts BELOW the h1, ABOVE the meta
   * rows, ONLY in the completed state and ONLY when a real actual exists. The
   * headline stays PLANNED (unchanged). Undefined (the default, and every flag-
   * off / no-actual / non-density path) → NOT rendered, so the hero is byte-
   * identical to today. over/match/under render at IDENTICAL weight — the state
   * never styles the line (frame §2). Only the density hero passes this.
   */
  completedSubline?: HeroActualSubline | null;
  /* ── Loop-hero slots (PR 2). Optional ReactNodes that nest INSIDE the
   *    hero card so the whole morning loop is one card. Render order:
   *    eyebrow → title → meta(+readiness) → reward → cta → coachLine →
   *    footer. Omitted (undefined) → the hero renders exactly as before,
   *    so existing callers are unaffected. ── */
  /** Biometric ReadinessSlot, rendered in the meta region. */
  readiness?: ReactNode;
  /** RewardStrip (streak · days-done · on-track), above the CTA. */
  reward?: ReactNode;
  /** MarkTodayCompleteButton, inside the card. */
  cta?: ReactNode;
  /** CoachLine — the one merged coach voice, bottom of the card. */
  coachLine?: ReactNode;
};

export type TodayHeroFirstRunProps = {
  variant: 'firstRun';
  title: string;
  body: string;
  ctas: HeroCTA[];
  /* PR 5 — first-run gets the empty-but-honest reward strip + the
   *  reserved readiness slot (direction §6), but NO cta/coachLine slots
   *  (the firstRun CTA row is the action). Omitted → unchanged. */
  readiness?: ReactNode;
  reward?: ReactNode;
};

/**
 * G1 (UX Session 3) — before_season variant. Rendered when the season
 * resolver state is `before_season` AND a macro plan already exists (a season
 * was created but Week 1 hasn't started locally yet). Replaces the prior reuse
 * of `firstRun` for this state, which falsely read "Let's build your first
 * week" above an already-rendered full season.
 */
export type TodayHeroBeforeSeasonProps = {
  variant: 'beforeSeason';
  /** First week's local date — formats the "{weekday}, {month day}" copy. */
  seasonStartDate: Date;
  /** Whole local days until Week 1 (today → start). Drives the countdown copy. */
  daysUntilStart: number;
  /** Season name, e.g. "Training Season 2026". */
  seasonName: string;
  /** Total weeks in the macro plan, e.g. 16. */
  weekCount: number;
  /** Ordered phase summary, e.g. "Base → Build → Comp → Taper". */
  phaseSummary: string;
  /** Primary CTA — usually "Open season plan" → /v2/plan. */
  planLink: { label: string; href: string };
  /** When false, a de-emphasized "Finish setup" ghost is offered. */
  setupComplete: boolean;
  readiness?: ReactNode;
  reward?: ReactNode;
};

/**
 * W1-TODAY-HERO (2.0 Today, spec 7.4) — the opt-in `variant="v2"` hero: the date
 * line above one card holding kicker, title, stat line, the day's text, the
 * action area and the interim coach line. Presentational: every string arrives
 * derived (`LoopHero` derives them from the session title and the week's days,
 * `lib/todayHeroAdapter.ts`), so this component owns layout, roles and the
 * required weather attribution, and nothing else. No reward strip, ring, streak
 * chip or status footer exists in this variant (TH-11).
 */
export type TodayHeroV2Props = {
  variant: 'v2';
  /** The date line above the card: "Tuesday, October 13" (TH-2). */
  date: Date;
  /** Kicker "Week N of M · Phase"; absent (no macro plan) renders no kicker. */
  weekOfTotal?: [number, number];
  /** Phase for the kicker's word, its 7px dot and the card's 3px rule. */
  phase?: SeasonPhase;
  /** The session name alone (TH-3). */
  title: string;
  /** "7 km" or "40 min"; null renders no stat line (TH-4). */
  stat?: string | null;
  /** String 18, "You ran 7.2 km · planned 7 km" (D7: only where the actuals gate allows). */
  completedLine?: string | null;
  /** String 19, "Effort 4 of 10". */
  effortLine?: string | null;
  /** The day's own text, under the stat (TH-6). */
  dayText?: string | null;
  /** Rest day: "Nothing to mark today." (string 26). */
  restLine?: string | null;
  /** Rest day: tomorrow's session, "Easy run, 6 km". Absent renders no Tomorrow row (D3). */
  tomorrow?: string | null;
  /** Only the Weather item is rendered (TH-13); every other item is ignored in this variant. */
  meta?: HeroMetaItem[];
  /** REL-8, as in the default variant: the Weather value came from Apple WeatherKit. */
  weatherAttribution?: boolean;
  /** The action area (TH-7 to TH-9). */
  actions?: ReactNode;
  /** The interim, event-only coach line (TH-12), after the action area. */
  coachLine?: ReactNode;
  /** The week-final logo celebration; takes no layout space (TH-11, Q8). */
  overlay?: ReactNode;
};

export type TodayHeroProps =
  | TodayHeroDefaultProps
  | TodayHeroFirstRunProps
  | TodayHeroBeforeSeasonProps
  | TodayHeroV2Props;

/**
 * Ask 3 (Claude Design return 2026-10-19, frame 3a), strings 12 to 14: the 2.0 hero's
 * Weather slot with no stored location. The hero is presentational, so whether the line
 * is wanted and what its two buttons do arrive through this context, which the page's
 * `HeroWeatherPromptHost` provides (null provider value, or no provider at all: no
 * prompt, and the hero is exactly what it was). Context rather than a prop because
 * `LoopHero` builds the v2 hero's props itself and forwards only a fixed few.
 */
export type WeatherPrompt = {
  /** "Add location": opens the Settings location modal. */
  onAdd: () => void;
  /** "Not now": hides the line for good on this account. */
  onDismiss: () => void;
};
export const WeatherPromptContext = createContext<WeatherPrompt | null>(null);

/** Strings 12, 13 and 14, byte for byte. */
export const WEATHER_PROMPT_COPY = {
  line: 'Add your location to see the weather for your runs.',
  add: 'Add location',
  notNow: 'Not now',
} as const;

/**
 * "Not now" hides the line for good on the ACCOUNT, so it is stored on the account's own
 * athlete context (`mergeContext`, the per-account store the Settings and Today writes
 * already use; the server's patch endpoint merges any key, no migration), as an ISO stamp
 * whose presence is the signal, like `_guardian_nudge_dismissed_at`.
 */
export const LOCATION_PROMPT_DISMISSED_KEY = '_location_prompt_dismissed_at';

/**
 * Whether the hero asks for a location: the 2.0 Today is on, the weather provider is on
 * (`weather_apple_v1`: with it off there is no weather to promise), the athlete's context
 * has been read for THIS account (an empty row that has not loaded is not an athlete with
 * no location), no usable coordinate is stored, and "Not now" has not been pressed.
 */
export function shouldPromptForLocation(args: {
  todayV2On: boolean;
  weatherOn: boolean;
  contextReady: boolean;
  raw: Record<string, unknown>;
}): boolean {
  const { todayV2On, weatherOn, contextReady, raw } = args;
  if (!todayV2On || !weatherOn || !contextReady) return false;
  if (contextCoord(raw._latitude) !== null && contextCoord(raw._longitude) !== null) return false;
  const dismissed = raw[LOCATION_PROMPT_DISMISSED_KEY];
  return !(typeof dismissed === 'string' && dismissed.trim() !== '');
}

const PHASE_LABEL: Record<SeasonPhase, string> = {
  base: 'Base',
  build: 'Build',
  competition: 'Competition',
  taper: 'Taper',
  recovery: 'Recovery',
};

const PHASE_CLASS: Record<SeasonPhase, string> = {
  base: styles.phasePillBase,
  build: styles.phasePillBuild,
  competition: styles.phasePillCompetition,
  taper: styles.phasePillTaper,
  recovery: styles.phasePillRecovery,
};

/**
 * "FRIDAY · MAY 8" — uppercased weekday + month + day. Stable across
 * locales via `Intl.DateTimeFormat`.
 */
function formatHeroDate(date: Date): string {
  const fmt = new Intl.DateTimeFormat(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
  // Intl returns e.g. "Friday, May 8" — re-shape to "FRIDAY · MAY 8".
  const parts = fmt.formatToParts(date);
  const weekday = parts.find((p) => p.type === 'weekday')?.value ?? '';
  const month = parts.find((p) => p.type === 'month')?.value ?? '';
  const day = parts.find((p) => p.type === 'day')?.value ?? '';
  return `${weekday} · ${month} ${day}`.toUpperCase();
}

/**
 * "GOOD AFTERNOON" / "GOOD MORNING" / "GOOD EVENING" — based on the same
 * `date`, so tests can pin the greeting deterministically by passing a
 * fixed Date.
 */
function greetingFor(date: Date): string {
  const h = date.getHours();
  if (h < 12) return 'GOOD MORNING';
  if (h < 17) return 'GOOD AFTERNOON';
  return 'GOOD EVENING';
}

/**
 * Compose the right-hand status text in the footer.
 *
 * Examples:
 *   weekOfTotal=[5,7], days=4, race='State Meet', mi=27 →
 *     "Week 5 of 7 · 4 days to State Meet · 27 mi this week"
 *   planNotDrafted=true →
 *     "Plan not yet drafted"
 */
function statusText(p: TodayHeroDefaultProps): string | null {
  if (p.planNotDrafted) return 'Plan not yet drafted';
  const parts: string[] = [];
  if (p.weekOfTotal) parts.push(`Week ${p.weekOfTotal[0]} of ${p.weekOfTotal[1]}`);
  if (typeof p.daysToNextRace === 'number') {
    const raceLabel = p.nextRaceName ? `to ${p.nextRaceName}` : 'to next meet';
    parts.push(
      `${p.daysToNextRace} ${p.daysToNextRace === 1 ? 'day' : 'days'} ${raceLabel}`,
    );
  }
  if (typeof p.weekMileage === 'number') parts.push(`${p.weekMileage} mi this week`);
  return parts.length > 0 ? parts.join(' · ') : null;
}

export function TodayHero(props: TodayHeroProps) {
  if (props.variant === 'firstRun') return <TodayHeroFirstRun {...props} />;
  if (props.variant === 'beforeSeason') return <TodayHeroBeforeSeason {...props} />;
  if (props.variant === 'v2') return <TodayHeroV2 {...props} />;
  return <TodayHeroDefault {...props} />;
}

function TodayHeroDefault(props: TodayHeroDefaultProps) {
  const iconsOn = useIconSystem();
  const dateLine = formatHeroDate(props.date);
  // B73 — three cases, and the middle one is the fix.
  //   · a name        → "GOOD EVENING, RAMIRO"   (unchanged)
  //   · '' (provided) → "GOOD EVENING"           (the caller signed in but the
  //     app knows no name for them; a greeting with nobody's name is still a
  //     greeting, and it is what replaced `GOOD EVENING, RAANROAL` — the
  //     uppercased local part of the founder's email address)
  //   · undefined     → date only                (prop omitted: demo hero,
  //     storybook, every existing test that renders without an athlete)
  const heroName = props.athleteName?.trim() ?? '';
  const greeting = heroName
    ? `${greetingFor(props.date)}, ${heroName.toUpperCase()}`
    : props.athleteName !== undefined
      ? greetingFor(props.date)
      : null;
  const eyebrowText = greeting ? `${dateLine} · ${greeting}` : dateLine;

  const status = statusText(props);
  const showFooter =
    !props.suppressFooter && (props.phase || status || props.planLink);

  // ⚖️ REL-8 option A — a quiet, linked Apple Weather mark on the surface that
  // displays the value. B61 moved it OUT of the meta row and onto its own legal
  // line at the bottom of the hero: as a `·`-separated meta item it was a
  // 10.5px underlined `white-space: nowrap` token wedged into a 14px
  // baseline-aligned kv row, so on a 375pt phone it could not wrap and pushed
  // the row (founder, Build 26: "looks bad, breaks visuals"). Same surface,
  // same link, legal-line register.
  //
  // TWO conditions, and both are load-bearing:
  //   · a Weather row is actually rendering (the mark credits a VALUE, and one
  //     floating over an absent row credits Apple for nothing);
  //   · `weatherAttribution` — the caller's statement that this weather came
  //     from Apple. The demo path (`?user=ramiro`) renders a hardcoded fixture
  //     with WeatherKit switched off entirely, and crediting Apple for a
  //     fixture is a small lie told in a legal notice.
  const showWeatherMark =
    !!props.weatherAttribution && !!props.meta?.some((m) => m.k === 'Weather');

  const metaBlock =
    props.meta && props.meta.length > 0 ? (
      <div className={styles.meta}>
        {props.meta.map((item, i) => (
          <span key={`${item.k ?? 'kv'}-${i}`} className={styles.kvWrap}>
            <span className={styles.kv}>
              {item.k && <span className={styles.k}>{item.k}</span>}
              {/* CD visual polish §1 — the condition glyph rides inside the
                  value, before the temperature. No glyph → the value stays a
                  plain inline text box, so every other meta row (and the
                  baseline alignment the k/v pair was fixed for) is
                  byte-identical to before. */}
              <span
                className={item.glyph ? `${styles.v} ${styles.vWithGlyph}` : styles.v}
              >
                {item.glyph && (
                  <span className={styles.vGlyph} aria-hidden>
                    <Icon name={item.glyph} size={14} />
                  </span>
                )}
                {item.v}
              </span>
            </span>
            {i < props.meta!.length - 1 && (
              <span className={styles.sep} aria-hidden>
                ·
              </span>
            )}
          </span>
        ))}
      </div>
    ) : null;

  // B61 — the attribution as a legal line, not a meta token. Bottom of the same
  // surface that renders the value, so the WeatherKit requirement ("attribution
  // where the data is displayed, linked to Apple's page") is still met.
  // ⛔ REMOVAL IS NOT AN OPTION. The Apple Developer Program License Agreement
  // requires the mark AND the link wherever WeatherKit data is shown. This can
  // be restyled and re-placed; it cannot be deleted.
  const weatherLegal = showWeatherMark ? (
    <p className={styles.weatherLegal}>
      <AppleWeatherMark />
    </p>
  ) : null;

  return (
    <section
      className={styles.todayHero}
      data-variant="default"
      // Ambient mode accent (direction §4) — the ::before left rule + the
      // eyebrow dot read their hue from this. No phase → neutral fallback.
      data-mode={props.phase ?? undefined}
    >
      <div className={styles.eyebrowRow}>
        <span className={styles.eyebrow}>
          <span className={styles.todayDot} aria-hidden />
          {eyebrowText}
        </span>
        {typeof props.streakDays === 'number' && props.streakDays > 0 && (
          <span className={styles.streakChip} aria-label={`${props.streakDays}-day streak`}>
            {/* CD-ICONS-01 — `chip.streak` @16. A MISSED MOUNT: the slot is
                literally called `.flame` and was rendering a filled circle.
                Same glyph the StreakChip and RewardStrip already use — one
                flame family (audit T-04). */}
            <span className={styles.flame} aria-hidden>
              {iconsOn ? <Icon name="chip.streak" size={16} /> : '●'}
            </span>{' '}
            {props.streakDays}-day streak
          </span>
        )}
      </div>

      <h1 className={styles.title}>{props.title}</h1>

      {/* B-19 completed-state sub-line (frame §1-§8) — quiet, factual, below the
          PLANNED headline. Renders ONLY when the caller (density hero, flag ON,
          today completed with a real actual) passes it; every other path leaves
          it undefined → byte-identical hero. The head ("You ran 5.0 mi") is one
          fixed weight/color for all three states; the `· planned N` tail is a
          quieter neutral step (§5) present only for over/under, never a valence
          cue. */}
      {props.completedSubline && (
        <p className={styles.completedActual}>
          <span className={styles.completedActualHead}>{props.completedSubline.head}</span>
          {props.completedSubline.tail && (
            <span className={styles.completedActualTailWrap}>
              <span className={styles.completedActualSep} aria-hidden>
                ·
              </span>{' '}
              <span className={styles.completedActualTail}>{props.completedSubline.tail}</span>
            </span>
          )}
        </p>
      )}

      {/* Meta region — meta row + the reserved readiness slot sit together
          (direction §1). Without a readiness slot the meta renders exactly
          as before (no wrapper) so existing callers are unaffected. */}
      {props.readiness ? (
        <div className={styles.metaRow}>
          {metaBlock}
          {props.readiness}
        </div>
      ) : (
        metaBlock
      )}

      {/* Loop-hero slots — nest the reward strip, CTA + coach voice inside
          the one card (undefined for non-loop callers → render nothing). */}
      {props.reward}
      {props.cta}
      {props.coachLine}

      {showFooter && (
        <div className={styles.statusFooter}>
          {props.phase && (
            <span
              className={`${styles.phasePill} ${PHASE_CLASS[props.phase]}`}
              role="status"
              aria-label={`${PHASE_LABEL[props.phase]} phase`}
            >
              <span className={styles.phaseDot} aria-hidden />
              {PHASE_LABEL[props.phase]}
            </span>
          )}
          {status && <span className={styles.metaText}>{status}</span>}
          {props.planLink && (
            <a className={styles.planLink} href={props.planLink.href}>
              {props.planLink.label} ›
            </a>
          )}
        </div>
      )}

      {/* B61 — legal line, last thing in the hero. */}
      {weatherLegal}
    </section>
  );
}

function TodayHeroV2(props: TodayHeroV2Props) {
  // TH-2: "Week N of M · Phase", label role; absent when there is no macro plan.
  const kicker = props.weekOfTotal
    ? [`Week ${props.weekOfTotal[0]} of ${props.weekOfTotal[1]}`, props.phase ? PHASE_LABEL[props.phase] : null]
        .filter(Boolean)
        .join(' · ')
    : null;

  // TH-13 (Q6): the weather row and its Apple Weather mark stay. Only Weather
  // survives in this variant (D2: no Pace row, and the intensity word is not shown).
  const weather = props.meta?.find((m) => m.k === 'Weather') ?? null;
  // ⛔ REMOVAL IS NOT AN OPTION (see the default variant): the mark is required
  // wherever WeatherKit data is shown. Same two conditions as the default hero.
  const showWeatherMark = !!props.weatherAttribution && !!weather;
  // Ask 3 (strings 12 to 14): with no weather value and a prompt provided, the same slot
  // asks for a location. Never on a rest day, and never beside a value (so no legal line).
  const weatherPrompt = useContext(WeatherPromptContext);
  const showWeatherPrompt = !weather && !!weatherPrompt && !props.restLine;

  return (
    <div className={styles.v2Root}>
      <p className={styles.v2Date}>{formatSeasonStart(props.date)}</p>
      <section
        className={`${styles.todayHero} ${styles.v2Card}`}
        data-variant="v2"
        // Same ambient phase rule and dot as the default hero (direction §4).
        data-mode={props.phase ?? undefined}
      >
        {kicker && (
          <p className={styles.v2Kicker}>
            <span className={styles.todayDot} aria-hidden />
            {kicker}
          </p>
        )}

        <h1 className={styles.v2Title}>{props.title}</h1>

        {props.stat && <p className={styles.v2Stat}>{props.stat}</p>}
        {props.completedLine && <p className={styles.v2Actual}>{props.completedLine}</p>}
        {props.effortLine && <p className={styles.v2Body}>{props.effortLine}</p>}
        {props.dayText && <p className={styles.v2Body}>{props.dayText}</p>}
        {props.restLine && <p className={styles.v2Body}>{props.restLine}</p>}

        {weather && (
          <p className={styles.v2Weather}>
            <span className={styles.v2Label}>{weather.k}</span>
            <span className={styles.v2WeatherValue}>
              {weather.glyph && (
                <span className={styles.vGlyph} aria-hidden>
                  <Icon name={weather.glyph} size={16} />
                </span>
              )}
              {weather.v}
            </span>
          </p>
        )}

        {showWeatherPrompt && weatherPrompt && (
          <div className={styles.v2WeatherPrompt} data-weather-prompt>
            <span className={styles.v2Label}>Weather</span>
            <p className={styles.v2WeatherPromptLine}>{WEATHER_PROMPT_COPY.line}</p>
            <div className={styles.v2WeatherPromptActions}>
              <button type="button" className={styles.v2WeatherAdd} onClick={weatherPrompt.onAdd}>
                {WEATHER_PROMPT_COPY.add}
              </button>
              <button type="button" className={styles.v2WeatherNotNow} onClick={weatherPrompt.onDismiss}>
                {WEATHER_PROMPT_COPY.notNow}
              </button>
            </div>
          </div>
        )}

        {props.tomorrow && (
          <div className={styles.v2Tomorrow}>
            <span className={styles.v2Label}>Tomorrow</span>
            <span className={styles.v2TomorrowValue}>{props.tomorrow}</span>
          </div>
        )}

        {props.actions}
        {props.coachLine}
        {props.overlay}

        {showWeatherMark && (
          <p className={styles.weatherLegal}>
            <AppleWeatherMark />
          </p>
        )}
      </section>
    </div>
  );
}

function TodayHeroFirstRun(props: TodayHeroFirstRunProps) {
  return (
    <section
      className={`${styles.todayHero} ${styles.firstRun}`}
      data-variant="firstRun"
      // No phase yet → the ambient rule stays neutral; the eyebrow dot is
      // the one warm --today "welcome" beat (direction §4).
      data-mode="firstrun"
    >
      <div className={styles.eyebrowRow}>
        <span className={styles.eyebrow}>
          <span className={styles.todayDot} aria-hidden />
          WELCOME
        </span>
      </div>

      <h1 className={styles.title}>{props.title}</h1>
      <p className={styles.firstRunBody}>{props.body}</p>

      {/* Reserved readiness slot + the empty-but-honest reward strip —
          present from day one so the runner sees the loop they're about
          to enter, without fake progress (decision 6). */}
      {props.readiness ? <div className={styles.metaRow}>{props.readiness}</div> : null}
      {props.reward}

      {props.ctas.length > 0 && (
        <div className={styles.ctaRow}>
          {props.ctas.map((cta, i) => {
            const cls = `${styles.btn} ${
              cta.variant === 'secondary' ? styles.btnSecondary : styles.btnPrimary
            }`;
            if (cta.href) {
              return (
                <a key={`${cta.label}-${i}`} className={cls} href={cta.href}>
                  {cta.label}
                </a>
              );
            }
            return (
              <button
                key={`${cta.label}-${i}`}
                className={cls}
                type="button"
                onClick={cta.onClick}
              >
                {cta.label}
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}

/**
 * "Saturday, May 31" — weekday + month + day, locale-stable via Intl.
 */
function formatSeasonStart(date: Date): string {
  const parts = new Intl.DateTimeFormat(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }).formatToParts(date);
  const weekday = parts.find((p) => p.type === 'weekday')?.value ?? '';
  const month = parts.find((p) => p.type === 'month')?.value ?? '';
  const day = parts.find((p) => p.type === 'day')?.value ?? '';
  return `${weekday}, ${month} ${day}`;
}

/**
 * Countdown copy variants (G1). Day-level granularity so the 1-day UTC/local
 * skew case — the one that actually ships — reads "starts tomorrow" rather
 * than "in 0 days". ≥14 days counts down in weeks; 2–13 in days; ≤1 = tomorrow
 * (the resolver flips to in_season at 0, so the hero unmounts before then).
 */
export function beforeSeasonCopy(
  daysUntilStart: number,
  seasonStartDate: Date,
): { eyebrow: string; countNum: string; countLabel: string; title: string } {
  const dateLabel = formatSeasonStart(seasonStartDate);
  if (daysUntilStart <= 1) {
    return {
      eyebrow: 'SEASON · STARTS TOMORROW',
      countNum: '1',
      countLabel: 'day until Week 1',
      title: 'Your season starts tomorrow.',
    };
  }
  if (daysUntilStart < 14) {
    return {
      eyebrow: `SEASON · STARTS IN ${daysUntilStart} DAYS`,
      countNum: String(daysUntilStart),
      countLabel: 'days until Week 1',
      title: `Your season starts ${dateLabel}.`,
    };
  }
  const weeks = Math.round(daysUntilStart / 7);
  return {
    eyebrow: `SEASON · STARTS ${dateLabel.toUpperCase()}`,
    countNum: String(weeks),
    countLabel: `${weeks === 1 ? 'week' : 'weeks'} until Week 1`,
    title: `Your season starts ${dateLabel}.`,
  };
}

function TodayHeroBeforeSeason(props: TodayHeroBeforeSeasonProps) {
  const copy = beforeSeasonCopy(props.daysUntilStart, props.seasonStartDate);
  return (
    <section
      className={`${styles.todayHero} ${styles.beforeSeason}`}
      data-variant="beforeSeason"
      // The hero wears the season it counts down to: the ::before left rule +
      // the eyebrow dot adopt the starting phase mode (base). (direction §4)
      data-mode="beforeseason"
    >
      <div className={styles.eyebrowRow}>
        <span className={styles.eyebrow}>
          <span className={styles.todayDot} aria-hidden />
          {copy.eyebrow}
        </span>
      </div>

      <h1 className={styles.title}>{copy.title}</h1>

      <div
        className={styles.countdown}
        role="status"
        aria-label={`${copy.countNum} ${copy.countLabel}`}
      >
        <span className={styles.countNum}>{copy.countNum}</span>
        <span className={styles.countLabel}>{copy.countLabel}</span>
      </div>

      <p className={styles.beforeSeasonMeta}>
        {props.seasonName} · {props.weekCount}-week plan
        {props.phaseSummary ? ` · ${props.phaseSummary}` : ''}
      </p>

      {/* Empty-but-honest loop preview — reserved readiness slot + the reward
          strip in its zero state. No fabricated streak/progress (decision 6). */}
      {props.readiness ? <div className={styles.metaRow}>{props.readiness}</div> : null}
      {props.reward}

      <div className={styles.ctaRow}>
        <a className={`${styles.btn} ${styles.btnPrimary}`} href={props.planLink.href}>
          {props.planLink.label}
        </a>
        {!props.setupComplete && (
          <button type="button" className={`${styles.btn} ${styles.btnSecondary}`}>
            Finish setup
          </button>
        )}
      </div>
    </section>
  );
}

export default TodayHero;
